/**
 * Kleda Client App - Type Definitions
 * Matches Salesforce KledaClientAppRest response structure
 */

// =============================================================================
// ORDER DATA - Direct mapping from Salesforce
// =============================================================================

/**
 * Eén afspraak, uit één bron. `label` en `vanaf`/`tot` horen bij elkaar:
 * bij `isDefinitief` komen ze van het smalle 2-uursblok van de gelockte route,
 * anders van het brede venster dat de klant zelf koos.
 */
export interface Afspraak {
  label?: string
  vanaf?: string        // ISO datetime
  tot?: string          // ISO datetime
  isDefinitief: boolean
}

export interface RouteOptie {
  id?: number         // 1, 2, of 3 (voor vooringevulde opties)
  label: string       // "Maandag 15 januari 09:00-12:00"
  routeId: string     // SF Route ID
  datum?: string      // ISO date (voor live opties)
  dagNaam?: string    // "maandag"
  dagNummer?: number  // 15
  maandNaam?: string  // "januari"
  tijdslot?: string   // "09:00 - 17:00"
  capaciteit?: number // Resterende capaciteit
  isNieuw?: boolean   // true = live query, false = vooringevuld
}

export type ProductCategory = 'Primary' | 'Package' | 'Extra' | string

// Service product codes (wijzigingskosten, annuleringskosten, etc.)
export const SERVICE_PRODUCT_CODES = [
  'WIJZIG-48-24',   // Wijziging 24-48 uur
  'WIJZIG-LT24',    // Wijziging < 24 uur
  'NOSHOW',         // Niet thuis
  'TRANSPORT',      // Transportkosten
  'ANNULERING',     // Annuleringskosten
] as const

export interface WasserijItem {
  id: string
  productNaam: string
  productCode?: string  // Product code (e.g. WIJZIG-LT24, NOSHOW)
  productCategory: ProductCategory
  lengte?: number       // cm
  breedte?: number      // cm
  diameter?: number     // cm (voor ronde kleden)
  oppervlakte?: number  // m²
  prijsInclBtw?: number  // Pre-korting incl BTW met extras (Originele_prijs_incl_BTW_met_Extras__c)
  aantal: number
  // Relatie naar primair item (voor packages en extra's)
  gerelateerdeItemId?: string
}

// Gegroepeerde weergave: primair item met gekoppelde packages/extras
export interface VloerkleedGroep {
  primair: WasserijItem
  pakket?: WasserijItem
  extras: WasserijItem[]
}

// Wijzig/annuleer kosten info
export interface WijzigKosten {
  bedrag: number
  tier: 'gratis' | '48-24' | '24-0' | 'niet-thuis'
  message: string
}

export interface OrderData {
  // Identificatie
  werkOrderId: string
  werkOrderNummer: string

  // Status
  status: string
  statusLabel: string
  phaseType: 'pickup' | 'delivery' | null

  // Zelf-halen-en-brengen-pad (Zelf_brengen__c / Zelf_halen__c, via de
  // mirror in de proxy). Sturen de zelf-views en de timeline-labels.
  zelfBrengen?: boolean
  zelfHalen?: boolean

  // Klant
  klantNaam: string

  // Adressen (plain text van SF formula fields)
  ophaalAdres: string
  afleverAdres: string

  // Contactgegevens + bewerk-context (self-service adres/telefoon-controle)
  mobiel?: string                // Mobiel__c — bewerkbaar
  telefoon?: string              // Telefoon__c (Account.Phone) — alleen tonen
  kanGegevensWijzigen?: boolean  // adres/telefoon nu corrigeerbaar?
  routeGelockt?: boolean         // actieve rit zit op een gelockte route (kalme hint)

  // Afspraak momenten
  ophaalMomentGekozen?: string
  ophaalMomentDefinitief?: string
  afleverMomentGekozen?: string
  afleverMomentDefinitief?: string
  ophalenVanaf?: string
  ophalenTot?: string
  afleverenVanaf?: string
  afleverenTot?: string

  // Afspraak als één geheel: label en tijdstippen uit dezelfde bron. Gebruik
  // dit voor alles dat met de klok te maken heeft — de losse velden hierboven
  // houden het brede klantvenster vast, ook als er al een definitief blok is.
  ophaalAfspraak?: Afspraak
  afleverAfspraak?: Afspraak

  // Route opties - vooringevuld (optie 1-3)
  routeOpties: RouteOptie[]
  alGekozen: boolean
  /**
   * Route_Keuze__c: het keuzenummer 1-3, niet de optie zelf. Stond hier als
   * `RouteOptie` getypeerd, waardoor twee views `gekozenOptie.label` lazen —
   * altijd undefined, en in de kaart-tak leverde dat een leeg wit blok op de
   * plek van de datum. De rit-sync wist het veld direct na de boeking, dus in
   * de praktijk is het bijna altijd null; het label hoort uit
   * `ophaalAfspraak`/`afleverAfspraak` te komen (09-09).
   */
  gekozenOptie?: number | null

  // Route opties - live query (6 extra opties)
  extraRouteOpties?: RouteOptie[]

  // Items
  items: WasserijItem[]
  totaalM2?: number
  totaalInclBtw?: number
  totaalOriginelePrijsInclBtw?: number  // Prijs zonder korting
  totaleKortingInclBtw?: number          // Kortingsbedrag
  openstaandBedrag?: number              // Werkelijk nog te betalen (Openstaand_Bedrag__c)

  // Opmerkingen
  opmerkingenOphalen?: string
  opmerkingenAfleveren?: string

  // Beschikbare acties
  kanAfspraakKiezen: boolean
  kanWijzigen: boolean
  kanAnnuleren: boolean

  // Wijzig/annuleer info
  urenTotAfspraak?: number
  wijzigKosten?: WijzigKosten

  // Opslagkosten berekening (alleen bij afleverfase)
  // ISO datetime string - moment dat aflevervoorstel is verstuurd
  // Frontend berekent: gratis eerste 28 dagen, daarna €10/week incl. BTW
  opslagkostenStartDatum?: string

  // Voorinspectie data (alleen wanneer voorinspectie gedaan)
  voorinspectie?: VoorinspectieData

  // Vlekkenbaas upsell (beschikbaar bij delivery fase)
  vlekkenbaas?: VlekkenbaasData

  // Betaling
  betaalLink?: string       // Mollie payment link (kan ontbreken: openBetaalLink mint hem dan op het betaalmoment)
  betaalStatus?: 'open' | 'betaald' | 'verlopen'
  opFactuur?: boolean       // Klant betaalt achteraf op factuur: geen betaalknop

  // Klacht-data (Fase 2)
  // Inline gevuld door KledaClientAppRest.buildKlachtSummary als er een open
  // klacht aan deze WO hangt. Toont de banner + KlachtView in client-app.
  klacht?: import('./lib/klacht-types').ClientKlacht | null

  // Vervolg-WO indicatoren (Fase 2)
  // Gevuld als deze WO een vervolg is op een eerdere klacht.
  // Triggert "Vervolg op uw klacht"-labels in OrderSummary + Timeline.
  klachtParentId?: string
  klachtParentNummer?: string
  isVervolgWo?: boolean
}

// =============================================================================
// API TYPES
// =============================================================================

export interface ApiError {
  code: string
  message: string
}

export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: ApiError
}

export type OrderResponse = ApiResponse<OrderData>

export interface KiesAfspraakRequest {
  token: string
  routeId: string         // Routes__c Id - altijd verplicht
  opmerkingen?: string
}

export type KiesAfspraakResponse = ApiResponse<OrderData>

export interface WijzigAfspraakRequest {
  token: string
  routeId: string         // Routes__c Id - altijd verplicht
  opmerkingen?: string
}

export type WijzigAfspraakResponse = ApiResponse<OrderData>

export interface AnnuleerRequest {
  token: string
  reden?: string
}

export interface AnnuleerResponseData {
  werkOrderId: string
  werkOrderNummer: string
  status: string
  statusLabel: string
  wijzigKosten?: WijzigKosten
  // Fee-WO velden (alleen aanwezig wanneer er annuleringskosten zijn)
  feeWerkOrderId?: string
  feeWerkOrderNummer?: string
  feePaymentUrl?: string
  feeAmount?: number
}

export type AnnuleerResponse = ApiResponse<AnnuleerResponseData>

// Reactiveer ("toch laten reinigen") — heropent een geannuleerde order
export interface ReactiveerRequest {
  token: string
}

export interface ReactiveerResponseData {
  werkOrderId: string
  werkOrderNummer: string
  status: string        // 'Ophalen plannen' na succes
  statusLabel: string
}

export type ReactiveerResponse = ApiResponse<ReactiveerResponseData>

export interface OpmerkingenRequest {
  token: string
  opmerkingenOphalen?: string
  opmerkingenAfleveren?: string
  verwachtOphalen?: string | null
  verwachtAfleveren?: string | null
}

export type OpmerkingenResponse = ApiResponse<OrderData>

// =============================================================================
// PRICING TYPES - For adding vloerkleden
// =============================================================================

export type VloerkleedVorm = 'rechthoekig' | 'rond' | 'overig'

export interface Package {
  sku: string
  label: string
  description?: string
  percentage: number      // bv 20 = +20% op basisprijs
  sortOrder: number
  isMeestGekozen: boolean
}

export interface BundleDiscount {
  label: string
  minCount: number
  discountPercent: number       // Als decimaal: 0.05 = 5%
  minAantalThreshold: number    // Min m² per kleed om mee te tellen
}

export interface PricingBootstrap {
  success: boolean
  errorCode?: string
  message?: string

  // Pricing catalog
  packages: Package[]
  bundleDiscounts: BundleDiscount[]
  regioFactor: number           // 1.0 = geen toeslag, 1.1 = 10% toeslag
  minimumOrderExBtw: number
  basePricePerM2: number        // Basis prijs per m² (ex BTW)

  // Order context
  bestaandeAantalKleden: number
  bestaandeTotaalM2: number
  kanToevoegen: boolean         // false als status >= Opgehaald
}

export interface VloerkleedConfig {
  vorm: VloerkleedVorm
  lengteCm?: number
  breedteCm?: number
  diameterCm?: number
  oppervlakteM2?: number        // Voor 'overig'
  packageSku: string
}

export interface AddVloerkleedRequest {
  token: string
  vorm: VloerkleedVorm
  lengteCm?: number
  breedteCm?: number
  diameterCm?: number
  oppervlakteM2?: number
  packageSku: string
}

export interface AddVloerkleedResponse {
  success: boolean
  errorCode?: string
  message?: string

  // Nieuw item
  itemId?: string
  oppervlakte?: number
  prijsExBtw?: number
  prijsInclBtw?: number  // Pre-korting incl BTW

  // Nieuwe order totalen
  nieuwTotaalM2?: number
  nieuwTotaalExBtw?: number
  nieuwTotaalInclBtw?: number

  // Bundelkorting
  heeftBundelkorting?: boolean
  bundelkortingPercentage?: number
  bundelkortingBedrag?: number
}

export interface EditVloerkleedRequest {
  token: string
  itemId: string
  vorm: VloerkleedVorm
  lengteCm?: number
  breedteCm?: number
  diameterCm?: number
  oppervlakteM2?: number
  packageSku: string
}

export interface EditVloerkleedResponse {
  success: boolean
  errorCode?: string
  message?: string

  itemId?: string
  oppervlakte?: number
  prijsExBtw?: number
  prijsInclBtw?: number

  nieuwTotaalM2?: number
  nieuwTotaalExBtw?: number
  nieuwTotaalInclBtw?: number
}

export interface DeleteVloerkleedRequest {
  token: string
  itemId: string
}

export interface DeleteVloerkleedResponse {
  success: boolean
  errorCode?: string
  message?: string

  nieuwTotaalM2?: number
  nieuwTotaalExBtw?: number
  nieuwTotaalInclBtw?: number
}

export interface UpdateAdresRequest {
  token: string
  straat: string     // Straat + huisnummer (incl. evt. toevoeging)
  postcode: string
  plaats: string
}

export interface UpdateAdresResponse {
  success: boolean
  errorCode?: string
  message?: string
  kant?: 'pickup' | 'delivery'
}

export interface UpdateTelefoonRequest {
  token: string
  mobiel: string
}

export interface UpdateTelefoonResponse {
  success: boolean
  errorCode?: string
  message?: string
  mobiel?: string   // genormaliseerd opgeslagen nummer
}

// =============================================================================
// VOORINSPECTIE TYPES
// =============================================================================

export interface VoorinspectieItem {
  id: string
  name: string
  m2: number
  lengte?: number
  breedte?: number
  diameter?: number
  prijsInclBtw?: number  // Pre-korting incl BTW met extras (Originele_prijs_incl_BTW_met_Extras__c)
  voorinspectie?: string          // Semicolon separated observations
  voorinspectieOpmerkingen?: string
  fotoUrl?: string
  pakketNaam?: string
  extras: string[]
  // Rand reparatie
  heeftLosseRanden?: boolean
  randReparatiePrijs?: number
  // Ondertapijt
  heeftOndertapijt?: boolean
  ondertapijtPrijs?: number
  ondertapijtPrijsMetKorting?: number
  tweedeligOndertapijt?: boolean   // Ondertapijt bestaat uit 2 delen (kleed >250 cm)
}

export interface RandReparatieData {
  eligible: boolean
  alreadyOrdered: boolean
  pricePerM2InclVat: number
  totalPriceInclVat: number
  items: VoorinspectieItem[]
}

export interface OndertapijtData {
  eligible: boolean
  alreadyOrdered: boolean
  pricePerM2InclVat: number
  discountPercentage: number
  totalPriceInclVat: number
  totalPriceWithDiscountInclVat: number
  savingsInclVat: number
  orderDeadline?: string       // ISO date — deadline to order (null = no deadline)
  deadlinePassed?: boolean     // True if too late to order
  items: VoorinspectieItem[]
  unavailableItemCount?: number    // Items niet beschikbaar (rond / voorraad)
  unavailableReason?: string       // Reden voor onbeschikbaarheid
}

export interface VoorinspectieData {
  done: boolean
  algemeenOpmerkingen?: string
  infoDiepVuil?: boolean
  infoLooppad?: boolean
  infoEnkeleVlekken?: boolean
  items: VoorinspectieItem[]
  randReparatie: RandReparatieData
  ondertapijt: OndertapijtData
}

// =============================================================================
// VLEKKENBAAS TYPES
// =============================================================================

export interface VlekkenbaasData {
  eligible: boolean
  alreadyOrdered: boolean
  orderedQuantity: number  // How many were ordered (0 if not ordered)
  priceInclVat: number
  productCode: string    // WAS-UPSELL-KLEDA-VLEKVERWIJDERAAR
}

export interface VlekkenbaasRequest {
  token: string
  confirm: boolean
  quantity?: number  // Number of sets (default 1, max 5)
}

export interface VlekkenbaasResponse {
  success: boolean
  message?: string
  errorCode?: string
  alreadyOrdered?: boolean
}

// =============================================================================
// UPSELL REQUEST/RESPONSE TYPES
// =============================================================================

export interface RandReparatieRequest {
  token: string
  confirm: boolean
  itemIds?: string[]  // Optional: specific items to repair
}

export interface RandReparatieResponse {
  success: boolean
  message?: string
  errorCode?: string
  alreadyOrdered?: boolean
}

export interface OndertapijtRequest {
  token: string
  confirm: boolean
  itemIds?: string[]  // Optional: specific items for ondertapijt
}

export interface OndertapijtResponse {
  success: boolean
  message?: string
  errorCode?: string
  alreadyOrdered?: boolean
}

// After-flow: nabestellen ná aflevering (nieuwe webshop-order + betaallink)
export interface OndertapijtNabestelResponse {
  success: boolean
  message?: string
  errorCode?: string
  werkOrderName?: string | null
  paymentUrl?: string | null
  subtotalCents?: number | null
  shippingCents?: number | null
  chargedCents?: number | null
}

// =============================================================================
// VIEW TYPES
// =============================================================================

export type ViewType =
  | 'kies-afspraak'    // Kies ophaal- of aflevermoment
  | 'al-gekozen'       // Afspraak gekozen, wacht op bevestiging
  | 'gepland'          // Afspraak staat gepland
  | 'in-behandeling'   // Items worden gereinigd
  | 'zelf-brengen'     // Klant brengt het kleed zelf naar de wasserij
  | 'zelf-halen'       // Kleed klaar, klant haalt het zelf op
  | 'afgerond'         // Order compleet
  | 'onbekend'         // Fallback
  | 'error'            // Fout

export interface ViewConfig {
  view: ViewType
  title: string
}

// =============================================================================
// STATUS MAPPING
// =============================================================================

export const STATUS_TO_VIEW: Record<string, ViewConfig> = {
  'Ophalen plannen': {
    view: 'kies-afspraak',
    title: 'Kies ophaalmoment',
  },
  'Ophaalvoorstel gedaan': {
    view: 'kies-afspraak',
    title: 'Kies ophaalmoment',
  },
  'Ophaalvoorstel geen reactie': {
    view: 'kies-afspraak',
    title: 'Kies ophaalmoment',
  },
  'Ophalen ingepland': {
    view: 'gepland',
    title: 'Ophalen gepland',
  },
  'Opgehaald': {
    view: 'in-behandeling',
    title: 'In behandeling',
  },
  'Aan het reinigen': {
    view: 'in-behandeling',
    title: 'Wordt gereinigd',
  },
  'Terugbrengen plannen': {
    view: 'kies-afspraak',
    title: 'Kies aflevermoment',
  },
  'Aflevervoorstel gedaan': {
    view: 'kies-afspraak',
    title: 'Kies aflevermoment',
  },
  'Terugbrengen ingepland': {
    view: 'gepland',
    title: 'Afleveren gepland',
  },
  'Teruggebracht': {
    view: 'afgerond',
    title: 'Afgerond',
  },

  // Zelf-halen-en-brengen-pad. Zelfde fase-indeling als api/client-app/_fase.ts
  // en Timeline.tsx. 'Afgerond' komt ook op route-orders voor en is daar net
  // zo goed het eindpunt.
  'Wordt gebracht': {
    view: 'zelf-brengen',
    title: 'Breng je vloerkleed',
  },
  'Zelf gebracht': {
    view: 'in-behandeling',
    title: 'In behandeling',
  },
  'Wordt opgehaald': {
    view: 'zelf-halen',
    title: 'Klaar om op te halen',
  },
  'Zelf opgehaald': {
    view: 'afgerond',
    title: 'Afgerond',
  },
  'Afgerond': {
    view: 'afgerond',
    title: 'Afgerond',
  },
}

// Niet-gemapte statussen mogen zich NOOIT voordoen als "opgehaald / in
// behandeling" (dat liet een geannuleerde order ten onrechte "opgehaald"
// tonen). Alle Kleda happy-path-statussen zitten in STATUS_TO_VIEW; een status
// die hier valt is een edge-/terminale status (On hold, Wachten op
// adresgegevens, ...). Die krijgt de neutrale OnbekendView i.p.v. een
// verzonnen ophaalmelding.
export const DEFAULT_VIEW_CONFIG: ViewConfig = {
  view: 'onbekend',
  title: 'Je bestelling',
}

export function getViewConfig(status: string): ViewConfig {
  return STATUS_TO_VIEW[status] || DEFAULT_VIEW_CONFIG
}
